import { useMemo } from 'react';
import { SelectInput } from '@interstate/components/SelectInput';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { PostalCodeField } from '@makemydeal/dr-shared-components/postal-code-field';
import { PhoneInput } from '@makemydeal/dr-shared-components/phone-input';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { Dropdown } from '@makemydeal/dr-shared-components';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { StateCodes, getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { FieldsRow, FieldSpan2 } from '../sectionLayout.styles';
import { DurationField } from './DurationField';
import { EMPLOYMENT_STATUS_OPTIONS, PAY_FREQUENCY_OPTIONS } from './housingAndEmployment.utils';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';
import type { CustomerFormValues } from '../../../types/CustomerCardTypes';

type EmploymentValues = NonNullable<CustomerFormValues['currentEmployment']>;
type EmploymentAddress = NonNullable<EmploymentValues['address']>;
type FinancialValues = NonNullable<CustomerFormValues['financialInfo']>;

type EmploymentFieldsProps = {
    type: 'current' | 'previous';
    values: EmploymentValues;
    errors: {
        status?: ErrorState;
        employerName?: ErrorState;
        occupation?: ErrorState;
        workPhone?: ErrorState;
        totalYearsEmployed?: ErrorState;
        totalMonthsEmployed?: ErrorState;
        address?: Partial<Record<keyof EmploymentAddress, ErrorState>>;
    };
    financialErrors?: Partial<Record<'income' | 'incomeFrequency', ErrorState>>;
    financialValues?: Pick<FinancialValues, 'income' | 'incomeFrequency'>;
    handleInputChange: (field: string, value: string) => void;
    handleNumberInputChange: (field: string, value: string) => void;
    disabled?: boolean;
};

const PII_INPUT_ATTR = { textInput: { dataAttr: { className: 'pii' } } };

export const EmploymentFields = ({
    type,
    values,
    errors,
    financialErrors,
    financialValues,
    handleInputChange,
    handleNumberInputChange,
    disabled = false
}: EmploymentFieldsProps) => {
    const fieldPath = (field: string) => `${type}Employment.${field}`;
    const stateOptions = useMemo(getStateCodesSelectInputValues, [StateCodes]);
    const selectOnFocusSelector = featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap;

    const handleStatusChange = (value: string | undefined) => {
        handleInputChange(fieldPath('status'), value ?? '');
    };

    const handleEmployerNameChange = (value: string) => {
        handleInputChange(fieldPath('employerName'), value);
    };

    const handleOccupationChange = (value: string) => {
        handleInputChange(fieldPath('occupation'), value);
    };

    const handleWorkPhoneChange = (e: InterstateOnChangeEvent<string>) => {
        handleInputChange(fieldPath('workPhone'), e.target.value);
    };

    const handleAddressChange = (field: string, value: string) => {
        handleInputChange(fieldPath(`address.${field}`), value);
    };

    const handleIncomeChange = (e: InterstateOnChangeEvent<string>) => {
        handleNumberInputChange('financialInfo.income', e.target.value);
    };

    const handlePayFrequencyChange = (value: string | undefined) => {
        handleInputChange('financialInfo.incomeFrequency', value ?? '');
    };

    // Memoize error objects
    const statusError = useMemo(() => errors.status || { hasError: false, errorMessage: '' }, [errors.status]);
    const employerNameError = useMemo(() => errors.employerName || { hasError: false, errorMessage: '' }, [errors.employerName]);
    const occupationError = useMemo(() => errors.occupation || { hasError: false, errorMessage: '' }, [errors.occupation]);
    const yearsError = useMemo(
        () => errors.totalYearsEmployed || { hasError: false, errorMessage: '' },
        [errors.totalYearsEmployed]
    );
    const monthsError = useMemo(
        () => errors.totalMonthsEmployed || { hasError: false, errorMessage: '' },
        [errors.totalMonthsEmployed]
    );

    const streetAddressError = useMemo(
        () => errors.address?.streetAddress || { hasError: false, errorMessage: '' },
        [errors.address?.streetAddress]
    );
    const cityError = useMemo(() => errors.address?.city || { hasError: false, errorMessage: '' }, [errors.address?.city]);
    const stateError = useMemo(() => errors.address?.state || { hasError: false, errorMessage: '' }, [errors.address?.state]);
    const zipError = useMemo(() => errors.address?.zip || { hasError: false, errorMessage: '' }, [errors.address?.zip]);

    const incomeError = useMemo(() => financialErrors?.income || { hasError: false, errorMessage: '' }, [financialErrors?.income]);
    const incomeFrequencyError = useMemo(
        () => financialErrors?.incomeFrequency || { hasError: false, errorMessage: '' },
        [financialErrors?.incomeFrequency]
    );

    return (
        <>
            <FieldsRow $columns={4} $mdColumns={2} $smColumns={1}>
                <SelectInput
                    name="status"
                    displayLabel
                    label="Employment Status"
                    value={values.status ?? ''}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    options={EMPLOYMENT_STATUS_OPTIONS}
                    disabled={disabled}
                    hasError={statusError.hasError}
                    errorMessage={statusError.errorMessage}
                    data-testid={`customer-form-${type}-status`}
                    size="small"
                />

                <TextInput
                    name="employerName"
                    displayLabel
                    label="Employer Name"
                    value={values.employerName ?? ''}
                    onChange={(e) => handleEmployerNameChange(e.target.value)}
                    disabled={disabled}
                    hasError={employerNameError.hasError}
                    errorMessage={employerNameError.errorMessage}
                    selectOnFocusSelector={selectOnFocusSelector}
                    data-testid={`customer-form-${type}-employer-name`}
                    size="small"
                    slotProps={PII_INPUT_ATTR}
                />

                <TextInput
                    name="occupation"
                    displayLabel
                    label="Occupation"
                    value={values.occupation ?? ''}
                    onChange={(e) => handleOccupationChange(e.target.value)}
                    disabled={disabled}
                    hasError={occupationError.hasError}
                    errorMessage={occupationError.errorMessage}
                    selectOnFocusSelector={selectOnFocusSelector}
                    data-testid={`customer-form-${type}-occupation`}
                    size="small"
                />

                <PhoneInput
                    name="workPhone"
                    displayLabel
                    label="Employer Phone"
                    value={values.workPhone ?? ''}
                    onChange={handleWorkPhoneChange}
                    disabled={disabled}
                    data-testid={`customer-form-${type}-work-phone`}
                    selectOnFocusSelector={selectOnFocusSelector}
                    size="small"
                />
            </FieldsRow>

            {type === 'current' ? (
                <FieldsRow $columns={4} $mdColumns={2} $smColumns={1}>
                    <FieldSpan2>
                        <TextInput
                            name="employerAddress"
                            displayLabel
                            label="Employer Address"
                            value={(values.address?.streetAddress as string) ?? ''}
                            onChange={(e) => handleAddressChange('streetAddress', e.target.value)}
                            disabled={disabled}
                            hasError={streetAddressError.hasError}
                            errorMessage={streetAddressError.errorMessage}
                            selectOnFocusSelector={selectOnFocusSelector}
                            data-testid="customer-form-employer-address"
                            size="small"
                            slotProps={PII_INPUT_ATTR}
                        />
                    </FieldSpan2>

                    <TextInput
                        name="employerCity"
                        displayLabel
                        label="City"
                        value={(values.address?.city as string) ?? ''}
                        onChange={(e) => handleAddressChange('city', e.target.value)}
                        disabled={disabled}
                        hasError={cityError.hasError}
                        errorMessage={cityError.errorMessage}
                        selectOnFocusSelector={selectOnFocusSelector}
                        data-testid="customer-form-employer-city"
                        size="small"
                        slotProps={PII_INPUT_ATTR}
                    />

                    <Dropdown
                        name="employerState"
                        displayLabel
                        label="State"
                        value={(values.address?.state as string) ?? ''}
                        onChange={(e) => handleAddressChange('state', String(e.target.value))}
                        options={stateOptions}
                        disabled={disabled}
                        hasError={stateError.hasError}
                        errorMessage={stateError.errorMessage}
                        selectOnFocusSelector={selectOnFocusSelector}
                        data-testid="customer-form-employer-state"
                        size="small"
                        allowRemoval
                    />

                    <PostalCodeField
                        name="employerZip"
                        displayLabel
                        label="Postal Code"
                        value={(values.address?.zip as string) ?? ''}
                        onChange={(e) => handleAddressChange('zip', e.target.value)}
                        disabled={disabled}
                        hasError={zipError.hasError}
                        errorMessage={zipError.errorMessage}
                        selectOnFocusSelector={selectOnFocusSelector}
                        data-testid="customer-form-employer-zip"
                        size="small"
                    />

                    <SelectInput
                        name="payFrequency"
                        displayLabel
                        label="Pay Frequency"
                        value={financialValues?.incomeFrequency ?? ''}
                        onChange={(e) => handlePayFrequencyChange(e.target.value)}
                        options={PAY_FREQUENCY_OPTIONS}
                        disabled={disabled}
                        hasError={incomeFrequencyError.hasError}
                        errorMessage={incomeFrequencyError.errorMessage}
                        data-testid="customer-form-pay-frequency"
                        size="small"
                    />

                    <NumericInput
                        name="expectedIncome"
                        displayLabel
                        label="Expected Income"
                        value={financialValues?.income}
                        onChange={handleIncomeChange}
                        allowDecimal
                        autoInsertCommas
                        decimalMaxLength={2}
                        decimalMinLength={0}
                        inputPrefix="$"
                        disabled={disabled}
                        hasError={incomeError.hasError}
                        errorMessage={incomeError.errorMessage}
                        selectOnFocusSelector={selectOnFocusSelector}
                        data-testid="customer-form-expected-income"
                        size="small"
                    />

                    <DurationField
                        label="Yrs. at Employer"
                        field={fieldPath('totalYearsEmployed')}
                        value={values.totalYearsEmployed}
                        error={yearsError}
                    />

                    <DurationField
                        label="Mos. at Employer"
                        field={fieldPath('totalMonthsEmployed')}
                        value={values.totalMonthsEmployed}
                        error={monthsError}
                    />
                </FieldsRow>
            ) : (
                <FieldsRow $columns={4} $mdColumns={2} $smColumns={1}>
                    <DurationField
                        label="Yrs. at Employer"
                        field={fieldPath('totalYearsEmployed')}
                        value={values.totalYearsEmployed}
                        error={yearsError}
                    />

                    <DurationField
                        label="Mos. at Employer"
                        field={fieldPath('totalMonthsEmployed')}
                        value={values.totalMonthsEmployed}
                        error={monthsError}
                    />
                </FieldsRow>
            )}
        </>
    );
};

export default EmploymentFields;
